import i18next from 'i18next';
import store from './store/index.js';
import { LANGUAGES } from './constants.js';

const debug = process.env.NODE_ENV !== 'production';

const loadResources = async (lng) => {
    try {
        const resources = await import(`../../locales/${lng}/translation.json`);
        return {
            [lng]: {
                translation: resources.default || resources,
            },
        };
    } catch (err) {
        console.error(`Failed to load language resources for ${lng}:`, err);
        return {};
    }
};

function normalizeLang(lng) {
    if (!lng) return 'en';

    const supported = LANGUAGES.map((l) => l.value);

    // try to do an exact match first so if xx-XX matches it will use it
    if (supported.includes(lng)) {
        return lng;
    }

    // our xx fallback incase no xx-XX match found
    const base = lng.split('-')[0];
    if (supported.includes(base)) {
        return base;
    }

    // the absolute fallback in case no languages match
    return 'en';
}

export async function setupI18n(app) {
    const language = normalizeLang(store.state.lang);

    const resources = await loadResources(language);

    await i18next.init({
        lng: language,
        fallbackLng: 'en',
        debug,
        resources: {
            [language]: {
                translation: resources[language]?.translation || {},
            },
        },
    });

    i18next.on('languageChanged', async function (lng) {
        console.debug('*** Language updated, reloading ***');
        const normalized = normalizeLang(lng);

        const resources = await loadResources(normalized);
        i18next.addResourceBundle(
            normalized,
            'translation',
            resources[normalized]?.translation || {},
            true,
            true,
        );

        // We need to reload the whole thing in order to get Google maps to pick up the new language
        // settings. This is in a setTimeout because otherwise it was not actually reloading the page.
        setTimeout(() => {
            window.location.reload(true);
        });
    });

    // Expose translation services as a globally available function for
    // ease of use in our components/templates.
    app.config.globalProperties.$t = function getTranslation(keys, options) {
        return i18next.t(keys, options);
    };
}
